import { motion } from 'framer-motion'
import { useNavigate } from 'react-router-dom'
import ChatIcon from '../../../assets/svgs/Chat'
import UserIcon from '../../../assets/svgs/User'
import FileIcon from '../../../assets/svgs/File'
import style from './HowItWorks.module.css'
import transparentVideo from '../../../assets/images/SinglePhoneScreen.webm'
import videoMobile from '../../../assets/images/mobileVideo.mp4'
import AutoPlaySilentVideo from '../video/Video'
import useWindowWidth from '../../../hooks/useWindowWidth'

const steps = [
  {
    number: '01',
    Icon: FileIcon,
    title: 'Drop your CV',
    text: 'Our AI maps your profile against 45,000 companies in Sweden — instantly finding where you fit and where you could go next.',
  },
  {
    number: '02',
    Icon: ChatIcon,
    title: 'Chat with the AI',
    text: "Drop any job or event into chat. Get a fit score, skill gaps, and the next concrete step — real data, not generic advice.",
  },
  {
    number: '03',
    Icon: UserIcon,
    title: 'Get your weekly shortlist',
    text: '20 curated opportunities every week, each ranked with a clear "why this, why now" and one action to take.',
  },
]

const cardVariants = {
  hidden: { opacity: 0, y: 28 },
  show: { opacity: 1, y: 0, transition: { duration: 0.5, ease: 'easeOut' } },
}

const HowItWorks = () => {
  const innerWidth = useWindowWidth()
  const navigate = useNavigate()

  return (
    <section className={style.section}>

      {/* Full-section transparent webm — phone slides in behind the cards */}
      {innerWidth > 1050 && (
        <video autoPlay muted className={style.bgVideo}>
          <source src={transparentVideo} type="video/webm" />
        </video>
      )}

      <div className={style.inner}>

        <motion.div
          className={style.header}
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.55 }}
        >
          <span className={style.eyebrow}>How it works</span>
          <h2 className={style.title}>Three steps to career clarity</h2>
          <p className={style.subtitle}>
            No scrolling. No feed. Just AI that reads 45,000 companies and hands you what's worth your time.
          </p>
        </motion.div>

        {innerWidth <= 1050 && (
          <AutoPlaySilentVideo className={style.mobileVideo} src={videoMobile} />
        )}

        <motion.div
          className={style.steps}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, amount: 0.2 }}
          transition={{ staggerChildren: 0.14 }}
        >
          {steps.map(({ number, Icon, title, text }) => (
            <motion.div key={number} className={style.stepCard} variants={cardVariants}>
              <div className={style.iconWrap}><Icon /></div>
              <div>
                <h3 className={style.stepTitle}>{title}</h3>
                <p className={style.stepText}>{text}</p>
              </div>
              <span className={style.stepNumber}>{number}</span>
            </motion.div>
          ))}
        </motion.div>

        <motion.div
          className={style.ctaRow}
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ delay: 0.5 }}
        >
          <motion.button
            className={style.button}
            whileHover={{ scale: 1.04 }}
            whileTap={{ scale: 0.97 }}
            onClick={() => {
              navigate('/?auth=login')
              window.scrollTo({ top: 0, behavior: 'smooth' })
            }}
          >
            Try it now →
          </motion.button>
        </motion.div>

      </div>
    </section>
  )
}

export default HowItWorks
